/* eslint-disable no-invalid-this */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import * as d3 from 'd3';
import _ from 'lodash';
import { BS_TOOLBAR_STATUS } from '../../../../comm/constant';
import { addNonOverlappingElement, isOverlapping } from '../../../../utils/newBedStatusUtil';
import { ReactComponent as Graphic } from '../../../../static/images/Graphic.svg';
import StyleButton from '../../../../components/StyleButton';
import { AddIcon } from '../../../../components/Icon';
import useUpdateEffect from '../../../../utils/useUpdateEffect';
import EADMemo from '../EADViewFloorPlan/components/EADMemo';
import EADNurseStation from '../EADViewFloorPlan/components/EADNurseStation';
import EADCubicle from '../EADViewFloorPlan/components/EADCubicle';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  graphic: {
    width: '9.375rem',
    height: '9.375rem',
  },
  addCubicle: {
    transform: 'translate(-6.25rem, -9rem)',
    overflow: 'auto',
  },
  addCubicleText: {
    fontFamily: 'NotoSans-Bold',
    color: '#707090',
    fontSize: '1rem',
  },
  floorRoot: ({ tool }) => ({
    cursor: tool.type === BS_TOOLBAR_STATUS.MOVE ? 'grab' : 'auto',
  }),
}));

const INTERACTIVE_TAGS = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'];
const CUBICLE_TYPE = {
  NURSE_STATION: 'Nurse Station/Misc.',
  MEMO: 'Memo',
};

const isInteractiveElement = (target) => {
  return Boolean(target?.closest?.(INTERACTIVE_TAGS.map((tagName) => tagName.toLowerCase()).join(',')));
};

const shouldHandleFloorPlanMove = (event) => {
  if (event.button) {
    return false;
  }

  return !isInteractiveElement(event.target);
};

export default function EditFloorPlan({
  tool,
  cubicleList,
  setCubicleList,
  handleShowCubiclePop,
  handleCopyCubicle,
  lastAddedCubicleId,
  viewType = '',
}) {
  const classes = useStyles({ tool });
  const svgRef = useRef();
  const gRef = useRef();
  const [d3Obj, setD3Obj] = useState({
    svg: null,
    g: null,
    cubicles: null,
  });
  const [updatedCubicle, setUpdatedCubicle] = useState({
    id: null,
    updateKey: 0,
  });
  const d3Zoom = d3.zoom();

  // 处理拖拽事件的逻辑
  const dragged = useCallback(
    function (event) {
      let nonOverlapping = true;
      const dragEl = d3.select(this);
      d3Obj.g
        .selectAll('foreignObject')
        .data(cubicleList)
        .each(function () {
          const otherEl = d3.select(this);
          const a = {
            x: event.x,
            y: event.y,
            width: Number(dragEl.attr('width')),
            height: Number(dragEl.attr('height')),
            id: dragEl.attr('id'),
          };

          const b = {
            x: Number(otherEl.attr('x')),
            y: Number(otherEl.attr('y')),
            width: Number(otherEl.attr('width')),
            height: Number(otherEl.attr('height')),
            id: otherEl.attr('id'),
          };

          if (isOverlapping(a, b)) {
            nonOverlapping = false;
          }
        });

      if (nonOverlapping) {
        const newX = Math.max(0, event.x);
        const newY = Math.max(0, event.y);
        d3.select(this).attr('x', newX).attr('y', newY);
      }
    },
    [cubicleList]
  );

  // 拖拽结束，更新数据
  const dragended = useCallback(
    function () {
      if (d3Obj.cubicles) {
        let tempCubicles = [];
        d3Obj.cubicles.each(function () {
          const foreignObject = d3.select(this);
          tempCubicles.push({
            id: Number(foreignObject.attr('id')),
            x: Number(foreignObject.attr('x')),
            y: Number(foreignObject.attr('y')),
            width: Number(foreignObject.attr('width')),
            height: Number(foreignObject.attr('height')),
          });
        });

        setCubicleList((oldData) => {
          const result = _.map(oldData, (item) => {
            if (_.some(tempCubicles, (tempItem) => tempItem.id === item.id)) {
              const tempCubicle = _.find(tempCubicles, (tempItem) => tempItem.id === item.id);
              return {
                ...item,
                x: tempCubicle.x,
                y: tempCubicle.y,
                width: tempCubicle.width,
                height: tempCubicle.height,
              };
            } else {
              return item;
            }
          });

          return result;
        });
      }
    },
    [d3Obj]
  );

  const handleUpdateCubicle = useCallback(
    ({ id, data }) => {
      setCubicleList((oldData) =>
        _.map(oldData, (item) =>
          item.id === id
            ? {
                ...item,
                data,
              }
            : item
        )
      );
      setUpdatedCubicle((oldData) => ({
        id,
        updateKey: oldData.updateKey + 1,
      }));
    },
    [setCubicleList]
  );

  const updateCubicleItem = useCallback((cubicle, bedItem, bedNo) => {
    return {
      ...cubicle,
      data: {
        ...cubicle.data,
        beds: _.map(cubicle.data?.beds, (item) => {
          return item.key === bedItem.key ? { ...item, bedName: bedNo } : item;
        }),
      },
    };
  }, []);

  const handleUpdateBedNo = useCallback(
    ({ cubicleId, bedItem, bedNo }) => {
      setCubicleList((prev) => {
        return _.map(prev, (cubicle) => {
          if (cubicle.id !== cubicleId) {
            return cubicle;
          }
          return updateCubicleItem(cubicle, bedItem, bedNo);
        });
      });
    },
    [setCubicleList]
  );

  const handleDeleteCubicle = useCallback(
    (id) => {
      setCubicleList((oldData) => _.filter(oldData, (item) => item.id !== id));
    },
    [setCubicleList]
  );

  const renderMemo = (cubicleItem) => (
    <EADMemo
      cubicleData={cubicleItem.data}
      id={cubicleItem.id}
      handleDeleteCubicle={handleDeleteCubicle}
      handleUpdateCubicle={handleUpdateCubicle}
      handleCopyCubicle={handleCopyCubicle}
      editMode={true}
    />
  );
  const renderCubicle = (cubicleItem) => (
    <EADCubicle
      cubicleData={cubicleItem.data}
      id={cubicleItem.id}
      handleDeleteCubicle={handleDeleteCubicle}
      handleUpdateCubicle={handleUpdateCubicle}
      handleCopyCubicle={handleCopyCubicle}
      handleUpdateBedNo={(bedNo, bedItem) => handleUpdateBedNo({ cubicleId: cubicleItem.id, bedNo, bedItem })}
      editMode={true}
      viewType={viewType}
    />
  );
  const renderNurseStation = (cubicleItem) => (
    <EADNurseStation
      cubicleData={cubicleItem.data}
      id={cubicleItem.id}
      handleDeleteCubicle={handleDeleteCubicle}
      handleUpdateCubicle={handleUpdateCubicle}
      handleCopyCubicle={handleCopyCubicle}
      editMode={true}
    />
  );
  const renderContent = (cubicleItem) => {
    if (cubicleItem?.data?.type === CUBICLE_TYPE?.NURSE_STATION) {
      return renderNurseStation(cubicleItem);
    } else if (cubicleItem?.data?.type === CUBICLE_TYPE?.MEMO) {
      return renderMemo(cubicleItem);
    } else {
      return renderCubicle(cubicleItem);
    }
  };

  // 添加拖拽事件
  useEffect(() => {
    if (tool.type === BS_TOOLBAR_STATUS.MOVE) {
      d3.select(gRef.current)
        .selectAll('foreignObject')
        .data(cubicleList)
        .call(d3.drag().filter(shouldHandleFloorPlanMove).on('drag', dragged).on('end', dragended));
    } else {
      d3.select(gRef.current)
        .selectAll('foreignObject')
        .data(cubicleList)
        .call(d3.drag().on('drag', null).on('end', null));
    }
  }, [dragged, dragended, cubicleList, tool]);

  const zoomed = useCallback(
    function ({ transform }) {
      const newX = Math.min(0, transform.x);
      const newY = Math.min(0, transform.y);
      const newTransform = d3.zoomIdentity.translate(newX, newY).scale(tool.zoom);
      d3Obj.g.attr('transform', newTransform);
    },
    [d3Obj.g, tool.zoom]
  );

  // 初始化 d3
  useEffect(() => {
    setD3Obj((o) => {
      o.svg = d3.select(svgRef.current);
      o.g = d3.select(gRef.current);
      o.cubicles = o.g.selectAll('foreignObject').data(cubicleList);
      return o;
    });
  }, [cubicleList]);

  // 添加 d3 zoom 事件
  useEffect(() => {
    d3Obj.svg.call(
      d3Zoom
        .filter((event) => {
          if (tool.type === BS_TOOLBAR_STATUS.MOVE) {
            return shouldHandleFloorPlanMove(event) && (event.type === 'mousedown' || event.type === 'touchstart');
          } else {
            return false;
          }
        })
        .scaleExtent([0.5, 2])
        .on('zoom', zoomed)
    );
  }, [d3Obj.svg, zoomed, tool.type]);

  // zoom-in, zoom-out
  useEffect(() => {
    const transform = d3.zoomTransform(gRef.current);
    const newX = Math.min(0, transform.x);
    const newY = Math.min(0, transform.y);
    const newTransform = d3.zoomIdentity.translate(newX, newY).scale(tool.zoom);
    d3Obj.g.attr('transform', newTransform);
  }, [tool.zoom, d3Obj.svg]);

  // reset
  useEffect(() => {
    d3Obj.svg.call(d3Zoom.transform, d3.zoomIdentity);
    const resetTransform = d3.zoomIdentity.translate(0, 0).scale(1);
    d3Obj.g.attr('transform', resetTransform);
  }, [tool.reset, d3Obj]);

  const getMeasuredCubicles = useCallback(() => {
    let tempCubicles = [];
    const foreignObjects = d3.select(gRef.current).selectAll('foreignObject');

    foreignObjects.each(function () {
      const foreignObject = d3.select(this);
      const childNode = foreignObject.select('div').node();

      if (!childNode) {
        return;
      }

      const width = childNode.offsetWidth;
      const height = childNode.offsetHeight;

      foreignObject.attr('width', width).attr('height', height);
      tempCubicles.push({
        id: Number(foreignObject.attr('id')),
        x: Number(foreignObject.attr('x')),
        y: Number(foreignObject.attr('y')),
        width: Number(foreignObject.attr('width')),
        height: Number(foreignObject.attr('height')),
      });
    });

    return tempCubicles;
  }, []);

  // 更新cubicle的实际宽高，避免重叠
  const updateCubicleLayoutSize = useCallback(
    ({ addedCubicleId = null, arrangedCubicleId = null } = {}) => {
      const tempCubicles = getMeasuredCubicles();

      if (tempCubicles.length === 0) {
        return;
      }

      setCubicleList((oldData) => {
        let hasChanged = false;
        const measuredCubicleMap = _.keyBy(tempCubicles, 'id');
        let result = _.map(oldData, (item) => {
          const tempCubicle = measuredCubicleMap[item.id];

          if (!tempCubicle) {
            return item;
          }

          if (item.id === addedCubicleId) {
            const newCubicle = addNonOverlappingElement(oldData.slice(0, -1), { ...tempCubicle, x: 0, y: 0 });
            hasChanged =
              hasChanged ||
              item.x !== newCubicle.x ||
              item.y !== newCubicle.y ||
              item.width !== newCubicle.width ||
              item.height !== newCubicle.height;
            return {
              ...item,
              x: newCubicle.x,
              y: newCubicle.y,
              width: newCubicle.width,
              height: newCubicle.height,
            };
          }

          hasChanged = hasChanged || item.width !== tempCubicle.width || item.height !== tempCubicle.height;
          return {
            ...item,
            width: tempCubicle.width,
            height: tempCubicle.height,
          };
        });

        if (arrangedCubicleId !== null) {
          const arrangedCubicles = [];
          const cubiclesById = {};

          _.forEach(_.orderBy(result, ['y', 'x'], ['asc', 'asc']), (cubicle) => {
            const arrangedCubicle =
              cubicle.id === arrangedCubicleId ? cubicle : addNonOverlappingElement(arrangedCubicles, { ...cubicle });
            arrangedCubicles.push(arrangedCubicle);
            cubiclesById[cubicle.id] = arrangedCubicle;
          });

          result = _.map(result, (item) => {
            const arrangedCubicle = cubiclesById[item.id];

            if (!arrangedCubicle) {
              return item;
            }

            hasChanged = hasChanged || item.x !== arrangedCubicle.x || item.y !== arrangedCubicle.y;
            return {
              ...item,
              x: arrangedCubicle.x,
              y: arrangedCubicle.y,
            };
          });
        }

        return hasChanged ? result : oldData;
      });
    },
    [getMeasuredCubicles, setCubicleList]
  );

  // 新增了cubicle之后按照实际宽度和高度更新数据
  useUpdateEffect(() => {
    if (lastAddedCubicleId === null) {
      return;
    }

    updateCubicleLayoutSize({ addedCubicleId: lastAddedCubicleId });
  }, [lastAddedCubicleId, updateCubicleLayoutSize]);

  // 修改 cubicle 行列后，foreignObject 需要跟随内容重新撑开并避开重叠。
  useUpdateEffect(() => {
    if (updatedCubicle.id === null) {
      return;
    }

    updateCubicleLayoutSize({ arrangedCubicleId: updatedCubicle.id });
  }, [updatedCubicle.updateKey, updateCubicleLayoutSize]);

  return (
    <Box className={classes.container}>
      <svg className={classes.floorRoot} ref={svgRef} width="100%" height="100%">
        <g ref={gRef}>
          {cubicleList.length === 0 && (
            <foreignObject className={classes.addCubicle} x="50%" y="50%" width="12.5rem" height="18rem">
              <Box display="flex" flexDirection="column" alignItems="center" gridGap="1.5rem">
                <Graphic className={classes.graphic} />
                <div className={classes.addCubicleText}>Let&apos;s start to add one.</div>
                <StyleButton
                  text="Add Cubicle"
                  Icon={AddIcon}
                  iconStyle={{ marginRight: '1rem' }}
                  width="100%"
                  type="fill"
                  onClick={handleShowCubiclePop}
                />
              </Box>
            </foreignObject>
          )}
          {cubicleList.map((item) => (
            <foreignObject
              key={item.id}
              id={item.id}
              x={item.x}
              y={item.y}
              width={item.width}
              height={item.height}
              style={{ overflow: 'visible' }}
            >
              {renderContent(item)}
            </foreignObject>
          ))}
        </g>
      </svg>
    </Box>
  );
}
